"use client";

import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import {
  CONSENT_GRANTED,
  CONSENT_NOT_GRANTED,
  isRecordedConsent,
  normalizeTrackingConsentCookie,
  writeTrackingConsent,
} from "@/lib/consent-cookie";
import { initializeGA4, updateGAConsent } from "@/lib/analytics";

export default function CookieConsent() {
  const [showBanner, setShowBanner] = useState(false);

  useEffect(() => {
    // Widens an already-recorded choice onto .salesably.ai so it carries to
    // salesably.ai, and reports the decision in force. Only a visitor who has
    // never chosen — and who is not signalling Global Privacy Control — sees
    // the banner. Nothing in here can throw; the guard is belt and braces so a
    // consent problem can never take the page down with it.
    try {
      const consent = normalizeTrackingConsentCookie();

      // Must be a value the contract recognises, not merely a non-empty one.
      // A cookie holding something else (hand-edited, or from some older
      // scheme) is not a choice this visitor made: treating it as one would
      // hide the banner while analytics stayed off, leaving them silently
      // denied with no way to answer. Showing the banner lets them choose, and
      // their answer overwrites the junk value.
      if (!isRecordedConsent(consent)) {
        setShowBanner(true);
      }
    } catch (error) {
      // Leave the banner down rather than risk re-prompting on a bad read.
    }
  }, []);

  const acceptCookies = () => {
    setShowBanner(false);

    try {
      writeTrackingConsent(CONSENT_GRANTED);

      // Update Datadog RUM consent if available
      if (window.DD_RUM && typeof window.DD_RUM.setTrackingConsent === "function") {
        window.DD_RUM.setTrackingConsent("granted");
      }

      updateGAConsent("granted");

      // Dispatch custom event to notify GoogleAnalyticsProvider
      window.dispatchEvent(new Event("consentChanged"));

      // Initialize GA4 after consent is granted
      initializeGA4();
    } catch (error) {
      // The banner is already dismissed; an analytics failure stops here.
    }
  };

  const declineCookies = () => {
    setShowBanner(false);

    try {
      writeTrackingConsent(CONSENT_NOT_GRANTED);

      // Update Datadog RUM consent if available
      if (window.DD_RUM && typeof window.DD_RUM.setTrackingConsent === "function") {
        window.DD_RUM.setTrackingConsent("not-granted");
      }

      updateGAConsent("denied");

      // Dispatch custom event to notify GoogleAnalyticsProvider
      window.dispatchEvent(new Event("consentChanged"));
    } catch (error) {
      // The banner is already dismissed; an analytics failure stops here.
    }
  };

  if (!showBanner) return null;

  return (
    <div className="fixed right-0 bottom-0 left-0 z-50 p-4 bg-white border-t border-gray-200 shadow-lg">
      <div className="container flex flex-col gap-4 justify-between items-center mx-auto sm:flex-row">
        <p className="text-sm text-gray-600">
          {`We use cookies and similar technologies to help personalize content, enhance your experience, and analyze our traffic. By clicking "Accept", you consent to our use of cookies.`}
        </p>
        <div className="flex gap-2">
          <Button variant="outline" onClick={declineCookies}>
            Decline
          </Button>
          <Button onClick={acceptCookies}>Accept</Button>
        </div>
      </div>
    </div>
  );
}
